import { Calendar } from '@tamagui/lucide-icons-2'
import {
    addMonths,
    eachDayOfInterval,
    endOfMonth,
    format,
    isSameDay,
    isToday,
    parse,
    startOfMonth,
    subMonths,
} from 'date-fns'
import { useEditMode } from 'purchasers/editMode'
import { omit } from 'ramda'
import { useState } from 'react'
import Button from 'components/button'
import { Popover, SizableText, View, XStack, YStack } from 'tamagui'
import { SlotWrapper } from './input'
import { Label } from './label'

interface DatePickerProps {
    value?: string | null
    onChange?: (value: string | null) => void
    label?: string
    placeholder?: string
    disabled?: boolean
    readOnly?: boolean
    error?: boolean | any
    minDate?: Date
    maxDate?: Date
    name?: string
    onBlur?: (e: any) => void
    [key: string]: any
}

export const DatePicker = ({
    value,
    onChange,
    label,
    placeholder = 'Select date',
    disabled,
    readOnly,
    error,
    minDate,
    maxDate,
    name,
    onBlur,
    ...props
}: DatePickerProps) => {
    const { editMode } = useEditMode()
    const [isOpen, setIsOpen] = useState(false)
    const [currentMonth, setCurrentMonth] = useState(() => {
        if (value) {
            try {
                return parse(value, 'yyyy-MM-dd', new Date())
            } catch {
                return new Date()
            }
        }
        return new Date()
    })

    // Parse the value to a Date object
    const selectedDate = value ? parse(value, 'yyyy-MM-dd', new Date()) : null

    // Format the display value
    const displayValue = selectedDate ? format(selectedDate, 'd MMM yyyy') : ''

    // View mode
    if (!editMode) {
        return (
            <YStack gap="$2">
                {label && <Label>{label}</Label>}
                <SizableText>{displayValue || '\u00A0'}</SizableText>
            </YStack>
        )
    }

    const handleDateSelect = (date: Date) => {
        const formattedDate = format(date, 'yyyy-MM-dd')
        onChange?.(formattedDate)
        setIsOpen(false)
    }

    const handleClear = () => {
        onChange?.(null)
        setIsOpen(false)
    }

    // Generate calendar days
    const monthStart = startOfMonth(currentMonth)
    const monthEnd = endOfMonth(currentMonth)
    const days = eachDayOfInterval({ start: monthStart, end: monthEnd })

    // Get the day of week for the first day (0 = Sunday, 6 = Saturday)
    const firstDayOfWeek = monthStart.getDay()

    // Fill in empty cells for days before the month starts
    const emptyDays = Array(firstDayOfWeek).fill(null)

    return (
        <YStack gap="$2">
            {label && <Label>{label}</Label>}

            <Popover
                open={isOpen}
                onOpenChange={setIsOpen}
                placement="bottom-start"
            >
                <Popover.Trigger asChild>
                    <XStack
                        width="100%"
                        borderWidth={1}
                        borderColor={error ? '$statusDanger' : '$borderColor'}
                        borderRadius="$4"
                        paddingHorizontal="$3.5"
                        paddingVertical="$2"
                        height={44}
                        backgroundColor="$backgroundStrong"
                        alignItems="center"
                        justifyContent="space-between"
                        cursor={disabled || readOnly ? 'default' : 'pointer'}
                        opacity={disabled || readOnly ? 0.6 : 1}
                        pointerEvents={disabled || readOnly ? 'none' : 'auto'}
                        hoverStyle={{
                            borderColor: error ? '$statusDanger' : '$borderColorHover',
                        }}
                        pressStyle={{
                            borderColor: error ? '$statusDanger' : '$borderColorFocus',
                        }}
                    >
                        <SizableText color={displayValue ? '$color' : '$colorMuted'}>
                            {displayValue || placeholder}
                        </SizableText>
                        <Calendar
                            size={16}
                            color="$colorMuted"
                        />
                    </XStack>
                </Popover.Trigger>

                <Popover.Content
                    borderWidth={1}
                    borderColor="$borderColor"
                    backgroundColor="$backgroundRaised"
                    padding="$3"
                    elevation={5}
                    animation="quick"
                    enterStyle={{ opacity: 0, scale: 0.95, y: -10 }}
                    exitStyle={{ opacity: 0, scale: 0.95, y: -10 }}
                >
                    <YStack
                        gap="$3"
                        width={280}
                    >
                        {/* Month navigation */}
                        <XStack
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <Button
                                size="$2"
                                chromeless
                                onPress={() => setCurrentMonth(subMonths(currentMonth, 1))}
                                icon={<SizableText>‹</SizableText>}
                            />
                            <SizableText fontWeight="600">{format(currentMonth, 'MMMM yyyy')}</SizableText>
                            <Button
                                size="$2"
                                chromeless
                                onPress={() => setCurrentMonth(addMonths(currentMonth, 1))}
                                icon={<SizableText>›</SizableText>}
                            />
                        </XStack>

                        {/* Day of week headers */}
                        <XStack gap="$1">
                            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                                <View
                                    key={day}
                                    width={36}
                                    alignItems="center"
                                >
                                    <SizableText
                                        size="$2"
                                        color="$colorMuted"
                                        fontWeight="600"
                                    >
                                        {day}
                                    </SizableText>
                                </View>
                            ))}
                        </XStack>

                        {/* Calendar grid */}
                        <View>
                            <XStack
                                flexWrap="wrap"
                                gap="$1"
                            >
                                {/* Empty cells for days before month starts */}
                                {emptyDays.map((_, i) => (
                                    <View
                                        key={`empty-${i}`}
                                        width={36}
                                        height={36}
                                    />
                                ))}

                                {/* Actual days */}
                                {days.map(day => {
                                    const isSelected = selectedDate && isSameDay(day, selectedDate)
                                    const isTodayDate = isToday(day)
                                    const isDisabled =
                                        (minDate && day < minDate) || (maxDate && day > maxDate)

                                    return (
                                        <Button
                                            key={day.toISOString()}
                                            size="$2"
                                            width={36}
                                            height={36}
                                            padding={0}
                                            chromeless={!isSelected}
                                            backgroundColor={isSelected ? '$primary' : 'transparent'}
                                            borderWidth={isTodayDate && !isSelected ? 1 : 0}
                                            borderColor="$primary"
                                            disabled={isDisabled}
                                            opacity={isDisabled ? 0.3 : 1}
                                            onPress={() => !isDisabled && handleDateSelect(day)}
                                            hoverStyle={{
                                                backgroundColor: isSelected ? '$primary' : '$backgroundHover',
                                            }}
                                        >
                                            <SizableText
                                                size="$3"
                                                color={isSelected ? 'white' : '$color'}
                                            >
                                                {format(day, 'd')}
                                            </SizableText>
                                        </Button>
                                    )
                                })}
                            </XStack>
                        </View>

                        {/* Action buttons */}
                        <XStack
                            gap="$2"
                            justifyContent="flex-end"
                        >
                            <Button
                                size="$2"
                                chromeless
                                onPress={handleClear}
                            >
                                Clear
                            </Button>
                            <Button
                                size="$2"
                                onPress={() => {
                                    handleDateSelect(new Date())
                                }}
                            >
                                Today
                            </Button>
                        </XStack>
                    </YStack>
                </Popover.Content>
            </Popover>
        </YStack>
    )
}

interface SlotDatePickerProps extends Omit<DatePickerProps, 'error'> {
    field: any
    formState: any
}

export const SlotDatePicker = ({ field, formState, ...props }: SlotDatePickerProps) => (
    <SlotWrapper
        field={field}
        formState={formState}
        formControlProps={{ width: '100%' }}
        {...props}
    >
        <DatePicker {...omit(['fieldState', 'label'], props)} />
    </SlotWrapper>
)
